import { useMemo } from "react";
import { useParams } from "react-router-dom";
import { useGetDialogueConfig } from "src/reusable/Chat/ChatStreaming/services/api";
import { ChatConfig } from "src/reusable/Chat/types";
import { AtomAssistChatPageParams } from "../AtomAssistChatPage";

const useGetConfigFromDialogueHistory = ({
  initialUserMessage,
  dialogueId: dialogueIdProp,
}: {
  initialUserMessage?: string;
  dialogueId?: number;
}) => {
  const { dialogueId: dialogueIdParam } = useParams<AtomAssistChatPageParams>();
  const dialogueId =
    dialogueIdProp !== undefined ? dialogueIdProp : Number(dialogueIdParam);

  const shouldEnableDialogueQuery = useMemo(() => {
    return (
      Number.isFinite(dialogueId) &&
      dialogueId > 0 &&
      initialUserMessage === undefined
    );
  }, [dialogueId, initialUserMessage]);

  const {
    data: dialogueConfig,
    isLoading: isDialogueLoading,
    isError: isDialogueError,
    isSuccess: isDialogueSuccess,
    error: dialogueError,
  } = useGetDialogueConfig({
    dialogueId: shouldEnableDialogueQuery ? dialogueId : null,
  });

  // Don't update store here - integrate dialogue history logic into useResolvePersona
  // This maintains single source of truth for all config/persona updates

  const chatConfig: Partial<ChatConfig> | null = useMemo(() => {
    if (!dialogueConfig) return null;

    // deduplicate datasets by ID
    const datasets = [
      ...(dialogueConfig?.datasets || []),
      ...(dialogueConfig?.persona?.datasets || []),
    ]
      .filter((dataset) => dataset && dataset.id)
      .reduce((map, dataset) => map.set(dataset.id, dataset), new Map());

    const result = {
      chatConfigId: dialogueConfig?.atom_assist_config_fk ?? undefined,
      toolCalling: true,
      datasets: Array.from(datasets.values()),
      chatControlMetadata: {},
      topSearchResults: dialogueConfig?.top_search_results,
      // Include persona information from dialogue history
      name: dialogueConfig?.persona?.name || "",
      slug: dialogueConfig?.persona?.slug || "",
      description: dialogueConfig?.persona?.description || "",
      initialPrompt:
        dialogueConfig?.persona?.initialPrompt ||
        (dialogueConfig?.persona as any)?.initial_prompt ||
        "",
      systemPrompt:
        dialogueConfig?.persona?.systemPrompt ||
        (dialogueConfig?.persona as any)?.system_prompt ||
        "",
      // Extract model capabilities from persona config
      modelCapabilities: dialogueConfig?.persona?.modelCapabilities ?? {},
    };

    return result;
  }, [dialogueConfig, dialogueId]);

  return {
    chatConfig,
    isLoading: shouldEnableDialogueQuery ? isDialogueLoading : false,
    isError: isDialogueError,
    isSuccess: isDialogueSuccess,
    error: dialogueError,
  };
};

export default useGetConfigFromDialogueHistory;
